import { useCallback, useEffect, useRef, useState } from 'react';

import { STEP_REORDER_CONTROL_ATTR } from 'constants/uiConstants';
import type { IInputStep } from 'interfaces/IInputStep';
import type { IInputTask } from 'interfaces/IInputTask';
import { InputTaskCategory } from 'interfaces/IInputTask';

import { hasDuplicateStepIds } from './hasDuplicateStepIds';
import { getTaskSteps } from './stepCalculations';
import { filterStepsByCategory } from './stepCategoryFilters';
import { moveVisibleStep } from './moveVisibleStep';
import { swapStepsById } from './swapStepsById';

/**
 * Placeholder payload for a step drag. Firefox refuses to start a drag unless
 * `dragstart` writes something to the DataTransfer, but that payload leaves the
 * app with the row — anything dropped on another window can read it — so it
 * deliberately carries no step, task or document identifier. The drop handler
 * resolves the dragged step from component state instead.
 */
const STEP_DRAG_PAYLOAD = 'input-step';

interface UseStepReorderParams {
  tasks: IInputTask[];
  activeStepCategoryKey: string;
  activeTaskCategory: InputTaskCategory;
  updateDraftTask: (task: IInputTask) => void;
  schedulePendingTaskSave: (task: IInputTask) => void;
  closeContextMenu: () => void;
  closeInputSelectors: () => void;
}

// Owns re-order mode (grip/keyboard-driven) and drag-and-drop for the step
// sub-table's rows — both express the same underlying action (swap two
// steps by id) through two different input methods.
export const useStepReorder = ({
  tasks,
  activeStepCategoryKey,
  activeTaskCategory,
  updateDraftTask,
  schedulePendingTaskSave,
  closeContextMenu,
  closeInputSelectors,
}: UseStepReorderParams) => {
  // The step currently picked up for re-ordering, if any. Keyboard focus for
  // the mode lives on the owning task's steps wrapper, never on the grip —
  // see RowGripButton for why.
  const [reorderTarget, setReorderTarget] = useState<{
    taskId: string;
    stepId: string;
  } | null>(null);
  // The step currently being dragged. Its identity lives in a ref rather than
  // state: the handlers read it, nothing renders it, and it has to survive the
  // whole gesture without re-rendering the grid out from under the drag.
  const dragStepRef = useRef<{ taskId: string; stepId: string } | null>(null);
  // The step whose grip the current press began on, which is what authorises a
  // drag. A ref because it is written during a native mousedown and read in the
  // dragstart that follows it, with no render in between.
  const dragOriginStepIdRef = useRef<string | null>(null);
  // A row only carries `draggable` while the pointer rests on its own grip, so
  // cells stay selectable everywhere else. This is the browser's gate on what
  // may be dragged, not the app's gate on what may be re-ordered — see
  // handleStepDragStart.
  const [armedDragStepId, setArmedDragStepId] = useState<string | null>(null);
  // The row a drag is currently hovering over, if it is a legal drop target.
  const [dragOverStepId, setDragOverStepId] = useState<string | null>(null);
  // One steps wrapper per expanded task, so entering reorder mode from either
  // the grip or the menu can focus the right one.
  const stepsWrapperRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Changing either filter can hide the step being re-ordered — the category
  // filter drops the row, the Plan/Actual tab drops the whole task and
  // unmounts its expanded row without firing a blur. Either way the mode would
  // be left pointing at something the user can no longer see or move.
  useEffect(() => {
    setReorderTarget(null);
  }, [activeStepCategoryKey, activeTaskCategory]);

  // A press that ends without becoming a drag is not a drag origin any more.
  // On the window rather than the grip, because the button is released wherever
  // the pointer happens to be — and a drag in flight produces no mouseup at
  // all, so this cannot fire between a genuine mousedown and its dragstart.
  useEffect(() => {
    const clearDragOrigin = () => {
      dragOriginStepIdRef.current = null;
    };
    window.addEventListener('mouseup', clearDragOrigin);
    return () => window.removeEventListener('mouseup', clearDragOrigin);
  }, []);

  const commitStepOrder = useCallback(
    (task: IInputTask, steps: IInputStep[] | null) => {
      // swapStepsById returns null for a swap that would change nothing.
      if (!steps) return;
      // The last line of defence for duplicated step ids. The grip, the menu
      // item and the drop are all gated already, so reaching here means one of
      // those gates has been missed — and a swap resolved by findIndex would
      // move a row the user did not point at, then persist it.
      if (hasDuplicateStepIds(getTaskSteps(task))) return;
      const updatedTask: IInputTask = { ...task, steps };
      updateDraftTask(updatedTask);
      schedulePendingTaskSave(updatedTask);
    },
    [updateDraftTask, schedulePendingTaskSave]
  );

  // Resolves the neighbour in the *visible* list but swaps by *absolute*
  // index, so steps hidden by the category filter keep their positions.
  const moveStep = useCallback(
    (taskId: string, stepId: string, direction: -1 | 1) => {
      const task = tasks.find((candidate) => candidate.taskId === taskId);
      if (!task) {
        setReorderTarget(null);
        return;
      }

      const allSteps = getTaskSteps(task);
      const visibleSteps = filterStepsByCategory(
        allSteps,
        activeStepCategoryKey
      );
      const visibleIndex = visibleSteps.findIndex(
        (step) => step.stepId === stepId
      );
      if (visibleIndex === -1) {
        setReorderTarget(null);
        return;
      }

      const neighbour = visibleSteps[visibleIndex + direction];
      // Already at one end of the visible list — a silent no-op.
      if (!neighbour) return;

      commitStepOrder(task, swapStepsById(allSteps, stepId, neighbour.stepId));
    },
    [tasks, activeStepCategoryKey, commitStepOrder]
  );

  const startReorder = useCallback(
    (taskId: string, stepId: string) => {
      closeInputSelectors();
      closeContextMenu();
      setReorderTarget({ taskId, stepId });
      // Focus the wrapper rather than the grip: the grip's own row is re-inserted
      // into the DOM when the step moves down, which would blur it.
      stepsWrapperRefs.current.get(taskId)?.focus({ preventScroll: true });
    },
    [closeInputSelectors, closeContextMenu]
  );

  const handleReorderKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!reorderTarget) return;

      if (event.key === 'Escape' || event.key === 'Enter') {
        event.preventDefault();
        setReorderTarget(null);
        return;
      }

      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      // Otherwise the workspace scrolls under the user on every move.
      event.preventDefault();
      // Each move rewrites the whole input document to localStorage, so a held
      // key would thrash it.
      if (event.repeat) return;

      moveStep(
        reorderTarget.taskId,
        reorderTarget.stepId,
        event.key === 'ArrowUp' ? -1 : 1
      );
    },
    [reorderTarget, moveStep]
  );

  const handleReorderBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      // relatedTarget is also null when the window itself loses focus — alt-tab,
      // a devtools panel, another browser tab. None of those are the user asking
      // to leave reorder mode, so only treat it as an exit while we still hold
      // document focus.
      if (!document.hasFocus()) return;
      // Only the wrapper and the grips host the mode; focusing a cell counts as clicking away
      const next = event.relatedTarget;
      if (
        next === event.currentTarget ||
        (next instanceof Element &&
          next.closest(`[${STEP_REORDER_CONTROL_ATTR}]`))
      ) {
        return;
      }
      setReorderTarget(null);
    },
    []
  );

  const registerStepsWrapper = useCallback(
    (taskId: string) => (node: HTMLDivElement | null) => {
      if (node) {
        stepsWrapperRefs.current.set(taskId, node);
      } else {
        stepsWrapperRefs.current.delete(taskId);
      }
    },
    []
  );

  // Dragging a step onto another lifts it out and inserts it there, shifting
  // the steps between (OP-2534 — previously a swap). moveVisibleStep re-orders
  // only the visible steps, so under a category filter the hidden ones keep
  // their positions. The arrow keys still move one place at a time, which is
  // the same as a swap with the neighbour.
  // Deliberately does not disarm the row: `armedDragStepId` is owned by the
  // grip's hover alone, and clearing it here would leave the handle under the
  // pointer unable to start another drag until the pointer left and returned.
  // That does mean a row can still be carrying `draggable` when a drag ends
  // under the pointer — which is why arming is not what authorises a drag.
  const clearStepDrag = useCallback(() => {
    dragStepRef.current = null;
    dragOriginStepIdRef.current = null;
    setDragOverStepId(null);
  }, []);

  // Written on the grip's native mousedown and read in the dragstart that
  // follows it, with no render in between — see dragOriginStepIdRef above.
  const setDragOrigin = useCallback((stepId: string) => {
    dragOriginStepIdRef.current = stepId;
  }, []);

  const setStepDragArmed = useCallback((stepId: string, armed: boolean) => {
    setArmedDragStepId((current) => {
      if (armed) return stepId;
      // Only the handle that armed the row may disarm it: leaving grip A after
      // the pointer has already reached grip B must not unarm B.
      return current === stepId ? null : current;
    });
  }, []);

  const handleStepDragStart = useCallback(
    (
      event: React.DragEvent<HTMLTableRowElement>,
      taskId: string,
      stepId: string
    ) => {
      // `dragstart` bubbles, so this fires for anything dragged out of the row
      // — a text selection pulled from a cell included — not only for the row
      // itself. The `draggable` attribute gates which elements the browser will
      // drag; only the press origin says whether the user took hold of the
      // handle, so that is what authorises the gesture. Cancelling here stops
      // the drag outright rather than letting it run and rejecting the drop.
      if (dragOriginStepIdRef.current !== stepId) {
        event.preventDefault();
        return;
      }
      event.stopPropagation();
      dragStepRef.current = { taskId, stepId };
      event.dataTransfer.effectAllowed = 'move';
      // Firefox will not begin a drag at all unless dragstart sets some data.
      // Nothing ever reads it back, so it carries an inert marker: the step id
      // is internal, and this payload is readable by whatever the row is
      // dropped on, including another application.
      event.dataTransfer.setData('text/plain', STEP_DRAG_PAYLOAD);
      // Everything anchored to a row that is about to move has to go: the
      // pointer and keyboard gestures are alternatives rather than companions,
      // and the actions menu and input picker are positioned against a <tr>
      // that the swap will relocate. (startReorder clears all of these but the
      // input picker, which looks like an oversight on that path rather than a
      // difference worth preserving here.)
      setReorderTarget(null);
      closeInputSelectors();
      closeContextMenu();
    },
    [closeInputSelectors, closeContextMenu]
  );

  const handleStepDragOver = useCallback(
    (
      event: React.DragEvent<HTMLTableRowElement>,
      taskId: string,
      stepId: string
    ) => {
      const dragged = dragStepRef.current;
      // Steps only re-order within their own task, and a row cannot be dropped
      // onto itself. Returning without preventDefault leaves the browser
      // showing "no drop" and stops a drop event ever firing here — and any
      // highlight left on the row the pointer came from has to go with it.
      if (!dragged || dragged.taskId !== taskId || dragged.stepId === stepId) {
        // dragover repeats for as long as the pointer rests here, so bail out
        // of the update rather than scheduling a render per event.
        setDragOverStepId((current) => (current === null ? current : null));
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      setDragOverStepId((current) => (current === stepId ? current : stepId));
    },
    []
  );

  // Dragging off the table altogether produces no further dragover, so without
  // this the last row hovered would stay lit until the drag ended.
  const handleStepDragLeave = useCallback(
    (event: React.DragEvent<HTMLTableRowElement>, stepId: string) => {
      // dragleave also fires when crossing between cells of the same row,
      // which is not leaving the row at all.
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
        return;
      }
      setDragOverStepId((current) => (current === stepId ? null : current));
    },
    []
  );

  const handleStepDrop = useCallback(
    (
      event: React.DragEvent<HTMLTableRowElement>,
      task: IInputTask,
      stepId: string
    ) => {
      event.preventDefault();
      event.stopPropagation();
      const dragged = dragStepRef.current;
      // Re-checked rather than taken on trust from dragover: moveVisibleStep
      // rejects a step from another task only because its id is missing from
      // this one's array, and a step dropped on itself only because the two
      // ids match — both are luck rather than a guard.
      if (
        dragged &&
        dragged.taskId === task.taskId &&
        dragged.stepId !== stepId
      ) {
        const allSteps = getTaskSteps(task);
        const visibleStepIds = new Set(
          filterStepsByCategory(allSteps, activeStepCategoryKey).map(
            (step) => step.stepId
          )
        );
        commitStepOrder(
          task,
          moveVisibleStep(allSteps, visibleStepIds, dragged.stepId, stepId)
        );
      }
      clearStepDrag();
    },
    [commitStepOrder, clearStepDrag, activeStepCategoryKey]
  );

  const reset = useCallback(() => {
    setReorderTarget(null);
  }, []);

  return {
    reorderTarget,
    armedDragStepId,
    dragOverStepId,
    startReorder,
    handleReorderKeyDown,
    handleReorderBlur,
    registerStepsWrapper,
    setStepDragArmed,
    setDragOrigin,
    handleStepDragStart,
    handleStepDragOver,
    handleStepDragLeave,
    handleStepDrop,
    clearStepDrag,
    reset,
  };
};
